import React, { useContext, useEffect, useRef } from 'react';
import { UseFormReturn, useFormState } from 'react-hook-form';
import { deepEqual, getValueByPath } from '@utils/helpers';
import {
    ATTACHMENTS_REQUIRED_MESSAGE,
    countEffectiveAttachments,
} from '@utils/validation/document/attachmentsValidation';
import { AttachFilesBlock, IAttachFilesBlockRefActions } from '@molecules/AttachFilesBlock/AttachFilesBlock';
import { IAttachesCreateModel } from '@models/attaches/IAttachesCreateModel';
import { ISelectedFiles } from '@molecules/AttachFilesBlock/ISelectedFiles';
import { useDebugPanel } from '@/context/DebugPanelContext';
import { DocumentImperativeValidatorsContext } from '@/context/Contexts';

export type IAttachFilesBlockControlProps<TFieldValues extends object = object> = {
    id?: string;
    attachModel: IAttachesCreateModel;
    /** Подпись под панелью (см. AttachFilesBlock.note). */
    note?: React.ReactNode;
    formMethods: UseFormReturn<TFieldValues>;
    name: string;
    /** Результат формулы `required` блока вложений формы. */
    required?: boolean;
    requiredMessage?: string;
};

const AttachFilesBlockControl = <TFieldValues extends object = object>({
    id = undefined,
    name,
    attachModel,
    note,
    formMethods,
    required = false,
    requiredMessage,
}: IAttachFilesBlockControlProps<TFieldValues>) => {
    const ctl = React.useRef<IAttachFilesBlockRefActions>(null);
    const prevValue = React.useRef<any>(undefined);
    const { settings: debugSettings } = useDebugPanel();
    const debugSettingsRef = useRef(debugSettings);
    debugSettingsRef.current = debugSettings;
    const message = requiredMessage?.trim() || ATTACHMENTS_REQUIRED_MESSAGE;
    // register вызывается один раз, поэтому правило читает актуальные значения через ref.
    const requiredRuleRef = useRef({ required, message });
    requiredRuleRef.current = { required, message };

    // Подписка на значения только своего поля: watch(callback) без имени заставлял RHF клонировать всю форму на
    // каждое событие любого поля.
    useEffect(
        () =>
            formMethods.subscribe({
                name: name as any,
                formState: { values: true },
                callback: ({ values }) => {
                    let data = getValueByPath(values, name);
                    if (data) {
                        if (!deepEqual(data, prevValue.current)) {
                            let copy = { ...data };
                            prevValue.current = copy;
                            ctl.current?.setData(copy);
                        }
                    }
                },
            }),
        [formMethods, name],
    );

    const checkValid = () => {
        try {
            if (debugSettingsRef.current.skipAllValidation) return true;
            return ctl.current?.isValid();
        } catch (error) {
            console.log(error);
            return true;
        }
    };

    /** Обязательность вне resolver-режима: под resolver-ом её считает buildDocumentResolver. */
    const checkRequired = (value: any) => {
        if (debugSettingsRef.current.skipAllValidation || debugSettingsRef.current.skipRequiredValidation) return true;
        const { required: isRequired, message: text } = requiredRuleRef.current;
        return !isRequired || countEffectiveAttachments(value) > 0 || text;
    };

    useEffect(() => {
        formMethods.register(name as any, { validate: { checkRequired, checkValid } });
    }, [formMethods.register]);

    // Под resolver-валидацией RHF игнорирует register-level validate — та же проверка
    // регистрируется у resolver-а как императивный валидатор (снятие при анмаунте).
    const imperativeValidators = useContext(DocumentImperativeValidatorsContext);
    const checkValidRef = useRef(checkValid);
    checkValidRef.current = checkValid;
    useEffect(() => imperativeValidators?.register(name, () => checkValidRef.current()), [imperativeValidators, name]);

    useEffect(() => {
        if (debugSettings.skipAllValidation) {
            formMethods.clearErrors(name as any);
        }
    }, [debugSettings.skipAllValidation, name, formMethods]);

    // formState мутируется по стабильной ссылке — подписку берём у useFormState,
    // иначе ошибка появлялась бы только со следующим сторонним рендером.
    const formState = useFormState({ control: formMethods.control, name: name as any });
    // Пустое сообщение — это императивный `checkValid`: ограничения блок показывает сам.
    const error = formMethods.getFieldState(name as any, formState).error?.message || undefined;

    return (
        <AttachFilesBlock
            id={id}
            ref={ctl}
            key={name}
            model={attachModel}
            note={note}
            required={required}
            error={error}
            onChanged={(data: ISelectedFiles) => {
                let copy = { ...data };
                prevValue.current = copy;
                formMethods.setValue(name as any, copy as any, { shouldDirty: true });
                // Требование снимается сразу после добавления файла, не дожидаясь сохранения.
                if (countEffectiveAttachments(copy) > 0) formMethods.clearErrors(name as any);
            }}
        />
    );
};

export default AttachFilesBlockControl;
